import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "@/config/axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Volume2,
  Loader2,
  RefreshCw,
  Info,
  Download,
  X,
  Check,
  AlertCircle,
  Settings,
  Play,
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  MessageSquare
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VoiceModels() {
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState("tts");
  
  // TTS state
  const [ttsText, setTtsText] = useState("");
  const [generatingSpeech, setGeneratingSpeech] = useState(false);
  const [generatedAudioUrl, setGeneratedAudioUrl] = useState(null);
  const [currentVoiceId, setCurrentVoiceId] = useState(null);
  const [currentVoiceName, setCurrentVoiceName] = useState(null);
  const [loadingVoiceInfo, setLoadingVoiceInfo] = useState(false);
  
  // Voice and Model selection
  const [availableVoices, setAvailableVoices] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [selectedModel, setSelectedModel] = useState("eleven_multilingual_v2");
  const [selectedModelData, setSelectedModelData] = useState(null);
  
  // Voice settings
  const [stability, setStability] = useState(0.5);
  const [similarity, setSimilarity] = useState(0.75);
  const [style, setStyle] = useState(0.0);
  const [speakerBoost, setSpeakerBoost] = useState(true);
  const [speed, setSpeed] = useState(1.0); // Speed: 0.25 to 4.0, default 1.0
  
  // Voice preview
  const [previewingVoice, setPreviewingVoice] = useState(false);
  const [previewAudioUrl, setPreviewAudioUrl] = useState(null);
  
  // AI Actor assignment
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [aiActors, setAiActors] = useState([]);
  const [selectedActorId, setSelectedActorId] = useState("");
  const [assigningVoice, setAssigningVoice] = useState(false);
  
  // Conversational AI state
  const [whisperModels, setWhisperModels] = useState([]);
  const [selectedWhisperModel, setSelectedWhisperModel] = useState("whisper-1");
  const [selectedCallActorId, setSelectedCallActorId] = useState("");
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callStatus, setCallStatus] = useState(""); // "connecting", "connected", "disconnected"
  const [conversationLog, setConversationLog] = useState([]);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false); // Track if AI is speaking
  const [conversationState, setConversationState] = useState("idle"); // "idle", "ready", "recording", "processing", "ai_speaking"
  const [isRecording, setIsRecording] = useState(false); // Track if currently recording (push-to-talk)
  const [sessionId, setSessionId] = useState(""); // Track session for chat history
  
  // Voice AI state
  const [voiceAIStatus, setVoiceAIStatus] = useState("idle"); // "idle", "connecting", "ready", "listening", "ai_speaking"
  const [voiceAIWebSocket, setVoiceAIWebSocket] = useState(null);
  const [voiceAISessionId, setVoiceAISessionId] = useState("");
  const [voiceAILog, setVoiceAILog] = useState([]);
  const [selectedVoiceAIActorId, setSelectedVoiceAIActorId] = useState("");
  const [voiceAIMode, setVoiceAIMode] = useState("openai"); // "openai" or "elevenlabs"
  const [selectedOpenAIVoice, setSelectedOpenAIVoice] = useState("alloy"); // OpenAI voice selection
  const [enableTrueBargeIn, setEnableTrueBargeIn] = useState(false); // True Barge-In setting from actor
  const [bargeInMode, setBargeInMode] = useState("keyword"); // "keyword" or "volume"
  const [bargeInKeywords, setBargeInKeywords] = useState(["hold on", "wait", "stop", "but", "however", "excuse me"]); // Barge-in keywords
  const [bargeInMessage, setBargeInMessage] = useState("Yes, please go ahead."); // Barge-in acknowledgment message
  
  // KB Source Tracking state (for test/debug display)
  const [showKBTracking, setShowKBTracking] = useState(false);
  const [kbTrackingInfo, setKBTrackingInfo] = useState({
    lastQuery: "",
    kbItemsUsed: [],
    threshold: 0.7,
    totalItems: 0,
    timestamp: null
  });
  
  // WebRTC refs
  const peerConnectionRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isCallActiveRef = useRef(false); // Use ref to avoid stale closure
  const isAiSpeakingRef = useRef(false); // Use ref to avoid stale closure
  const currentAudioRef = useRef(null); // Track current audio element
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const isProcessingRef = useRef(false); // Prevent parallel processing
  
  // Voice AI refs
  const voiceAIStreamRef = useRef(null);
  const voiceAIAudioContextRef = useRef(null); // For AI audio playback
  const voiceAICaptureContextRef = useRef(null); // For microphone capture (separate context)
  const voiceAIProcessorRef = useRef(null);
  const voiceAIPlaybackQueueRef = useRef([]);
  const voiceAIIsPlayingRef = useRef(false);
  const voiceAIStatusRef = useRef("idle");  // Ref to avoid stale closures
  const enableTrueBargeInRef = useRef(false);  // Ref for barge-in setting to avoid stale closures
  const bargeInModeRef = useRef("keyword");  // Ref for barge-in mode
  const bargeInKeywordsRef = useRef(["hold on", "wait", "stop", "but", "however", "excuse me"]);  // Ref for keywords
  const bargeInMessageRef = useRef("Yes, please go ahead.");  // Ref for barge-in message
  const voiceAICurrentSourceRef = useRef(null);  // Ref to track current audio source for barge-in
  const voiceAIBargeInActiveRef = useRef(false);  // Flag to prevent playback during barge-in
  const voiceAISpeechRecognitionRef = useRef(null);  // Web Speech API for keyword detection

  useEffect(() => {
    fetchCurrentVoiceInfo();
    fetchAvailableVoices();
    fetchAvailableModels();
    fetchAIActors();
    fetchWhisperModels();
  }, []);

  const fetchCurrentVoiceInfo = async () => {
    setLoadingVoiceInfo(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/settings/communication/elevenlabs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const elevenlabsSettings = response.data;
      if (elevenlabsSettings) {
        const voiceId = elevenlabsSettings.cloned_voice_id || elevenlabsSettings.default_voice_id;
        setCurrentVoiceId(voiceId);
        setCurrentVoiceName(elevenlabsSettings.cloned_voice_name || "Default Voice");
        setSelectedVoiceId(voiceId); // Set as default selection
      }
    } catch (error) {
      console.error("Failed to fetch voice info:", error);
    } finally {
      setLoadingVoiceInfo(false);
    }
  };

  const fetchAvailableVoices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/elevenlabs/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableVoices(response.data.voices || []);
    } catch (error) {
      console.error("Failed to fetch voices:", error);
    }
  };

  const fetchAvailableModels = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/elevenlabs/models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only TTS models
      const ttsModels = (response.data.models || []).filter(model => model.can_do_text_to_speech);
      setAvailableModels(ttsModels);
      
      // Set default model data
      const defaultModel = ttsModels.find(m => m.model_id === "eleven_multilingual_v2");
      if (defaultModel) {
        setSelectedModelData(defaultModel);
      }
    } catch (error) {
      console.error("Failed to fetch models:", error);
    }
  };

  const fetchWhisperModels = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/whisper-models`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWhisperModels(response.data.models || []);
    } catch (error) {
      console.error("Failed to fetch Whisper models:", error);
      // Set default models if API fails
      setWhisperModels([
        { id: "whisper-1", name: "Whisper-1", description: "General purpose, balanced speed and accuracy", tag: "Recommended" }
      ]);
    }
  };

  const fetchAIActors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/actors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiActors(response.data || []);
    } catch (error) {
      console.error("Failed to fetch AI Actors:", error);
    }
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    const modelData = availableModels.find(m => m.model_id === modelId);
    setSelectedModelData(modelData);
  };

  const previewVoice = async () => {
    if (!selectedVoiceId) {
      toast.error("Please select a voice first");
      return;
    }

    setPreviewingVoice(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(`${API}/ai/text-to-speech`,
        { 
          text: "Hello! This is a preview of this voice. You can use this to test how different voices sound before generating your actual content.",
          voice_id: selectedVoiceId,
          model_id: selectedModel,
          stability: stability,
          similarity_boost: similarity,
          style: style,
          use_speaker_boost: speakerBoost,
          speed: speed
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const audioUrl = `${BACKEND_URL}${response.data.audio_url}`;
      setPreviewAudioUrl(audioUrl);
      
      // Play the audio automatically
      const audio = new Audio(audioUrl);
      audio.play();
      
      toast.success("Voice preview generated!");
    } catch (error) {
      console.error("Failed to preview voice:", error);
      toast.error(error.response?.data?.detail || "Failed to preview voice");
    } finally {
      setPreviewingVoice(false);
    }
  };

  // Group voices by category
  const groupedVoices = {
    cloned: availableVoices.filter(v => v.category === "cloned"),
    professional: availableVoices.filter(v => v.category === "professional"),
    premade: availableVoices.filter(v => v.category === "premade")
  };

  const assignVoiceToActor = async () => {
    if (!selectedActorId) {
      toast.error("Please select an AI Actor");
      return;
    }

    if (!selectedVoiceId) {
      toast.error("Please select a voice first");
      return;
    }

    setAssigningVoice(true);
    try {
      const token = localStorage.getItem("token");
      const selectedVoice = availableVoices.find(v => v.voice_id === selectedVoiceId);
      
      await axiosInstance.post(`${API}/ai/actors/${selectedActorId}/assign-voice`,
        {
          actor_id: selectedActorId,
          voice_id: selectedVoiceId,
          voice_name: selectedVoice?.name || "Unknown Voice",
          voice_model: selectedModel,
          voice_stability: stability,
          voice_similarity: similarity,
          voice_speed: speed,
          voice_style: style,
          voice_speaker_boost: speakerBoost
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Voice settings assigned to AI Actor successfully!`);
      setShowAssignDialog(false);
      setSelectedActorId("");
    } catch (error) {
      console.error("Failed to assign voice:", error);
      toast.error(error.response?.data?.detail || "Failed to assign voice to actor");
    } finally {
      setAssigningVoice(false);
    }
  };

  const generateSpeech = async () => {
    if (!ttsText.trim()) {
      toast.error("Please enter text to generate speech");
      return;
    }

    if (!selectedVoiceId) {
      toast.error("Please select a voice");
      return;
    }

    setGeneratingSpeech(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.post(`${API}/ai/text-to-speech`,
        { 
          text: ttsText,
          voice_id: selectedVoiceId,
          model_id: selectedModel,
          stability: stability,
          similarity_boost: similarity,
          style: style,
          use_speaker_boost: speakerBoost,
          speed: speed
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedAudioUrl(`${BACKEND_URL}${response.data.audio_url}`);
      toast.success("Speech generated successfully!");
    } catch (error) {
      console.error("Failed to generate speech:", error);
      toast.error(error.response?.data?.detail || "Failed to generate speech");
    } finally {
      setGeneratingSpeech(false);
    }
  };

  // Conversational AI Functions
  const startCall = async () => {
    if (!selectedCallActorId) {
      toast.error("Please select an AI Actor");
      return;
    }
    
    if (!selectedVoiceId) {
      toast.error("Please select a voice");
      return;
    }

    setCallStatus("connecting");
    setIsCallActive(true);
    isCallActiveRef.current = true; // Set ref
    setConversationLog([]);
    
    // Generate new session ID for this call
    const newSessionId = `voice_${selectedCallActorId}_${Date.now()}`;
    setSessionId(newSessionId);

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      
      setCallStatus("connected");
      setConversationState("ready"); // Ready for push-to-talk
      toast.success("Call connected! Hold 'Push to Talk' button and speak");
      
    } catch (error) {
      console.error("Failed to start call:", error);
      toast.error("Failed to access microphone");
      setCallStatus("disconnected");
      setIsCallActive(false);
      isCallActiveRef.current = false;
    }
  };

  const endCall = () => {
    // Stop recording if active
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    
    // Stop all streams
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    
    // Stop any playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.currentTime = 0;
      currentAudioRef.current = null;
    }
    
    setCallStatus("disconnected");
    setIsCallActive(false);
    isCallActiveRef.current = false;
    setIsAiSpeaking(false);
    isAiSpeakingRef.current = false;
    isProcessingRef.current = false;
    setIsMuted(false);
    setConversationState("idle");
    setIsRecording(false);
    toast.info("Call ended");
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  // Push-to-Talk: Start recording when button is pressed
  const startPushToTalk = () => {
    if (!localStreamRef.current) return;
    if (isAiSpeakingRef.current) {
      toast.warning("AI is speaking, please wait");
      return;
    }
    if (isProcessingRef.current) {
      toast.warning("Still processing previous message");
      return;
    }

    console.log("🎤 Push-to-Talk: Started recording");
    setIsRecording(true);
    setConversationState("recording");
    
    const mediaRecorder = new MediaRecorder(localStreamRef.current);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.start();
  };

  // Push-to-Talk: Stop recording and process when button is released
  const stopPushToTalk = async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") {
      return;
    }

    console.log("🛑 Push-to-Talk: Stopped recording, processing...");
    setIsRecording(false);
    
    mediaRecorderRef.current.onstop = async () => {
      if (audioChunksRef.current.length > 0) {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        if (audioBlob.size > 5000) { // At least 5KB of audio
          console.log("✅ Processing audio (", (audioBlob.size / 1024).toFixed(1), "KB)");
          setConversationState("processing");
          isProcessingRef.current = true;
          
          try {
            await processAudio(audioBlob);
          } catch (err) {
            console.error("Error in processAudio:", err);
            toast.error("Error processing audio");
          } finally {
            isProcessingRef.current = false;
            if (!isAiSpeakingRef.current) {
              setConversationState("ready");
            }
          }
        } else {
          console.log("⚠️ Audio too short, skipping");
          setConversationState("ready");
          toast.warning("Audio too short, please try again");
        }
        
        audioChunksRef.current = [];
      }
    };

    mediaRecorderRef.current.stop();
  };

  const processAudio = async (audioBlob) => {
    try {
      const token = localStorage.getItem("token");
      
      console.log("🎤 Processing audio chunk...");
      
      // Step 1: Speech to Text (Whisper)
      const formData = new FormData();
      formData.append('audio', audioBlob, 'audio.webm');
      formData.append('model', selectedWhisperModel);
      
      console.log("📡 Sending audio to Whisper STT...");
      const sttResponse = await axiosInstance.post(`${API}/ai/speech-to-text`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const userText = sttResponse.data.text;
      console.log("✅ Transcribed text:", userText);
      
      if (!userText || userText.trim().length === 0) {
        console.log("⚠️ No text detected, skipping...");
        return;
      }
      
      // Add to conversation log
      setConversationLog(prev => [...prev, { role: 'user', text: userText }]);
      
      // Step 2: Get AI Actor response (with chat history)
      console.log("🤖 Sending to AI Actor for response...");
      const llmResponse = await axiosInstance.post(`${API}/ai/conversational-chat/${selectedCallActorId}`, {
        message: userText,
        session_id: sessionId  // Include session_id for chat history
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const aiText = llmResponse.data.response;
      console.log("✅ AI Response:", aiText);
      setConversationLog(prev => [...prev, { role: 'assistant', text: aiText }]);
      
      // Step 3: Text to Speech (ElevenLabs)
      console.log("🔊 Generating speech from AI response...");
      const ttsResponse = await axiosInstance.post(`${API}/ai/text-to-speech`, {
        text: aiText,
        voice_id: selectedVoiceId,
        model_id: selectedModel,
        stability: stability,
        similarity_boost: similarity,
        style: style,
        use_speaker_boost: speakerBoost,
        speed: speed
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log("✅ TTS Response:", ttsResponse.data);
      
      // Step 4: Play audio response
      const audioUrl = `${BACKEND_URL}${ttsResponse.data.audio_url}`;
      console.log("🎵 Playing audio from:", audioUrl);
      
      // STOP any currently playing audio first
      if (currentAudioRef.current) {
        console.log("⏹️ Stopping previous audio before playing new one");
        currentAudioRef.current.pause();
        currentAudioRef.current.currentTime = 0;
        currentAudioRef.current = null;
      }
      
      // Set AI speaking flag BEFORE playing
      setIsAiSpeaking(true);
      isAiSpeakingRef.current = true;
      setConversationState("ai_speaking");
      
      const audio = new Audio(audioUrl);
      currentAudioRef.current = audio;
      
      // Add event listeners for debugging and state management
      audio.addEventListener('canplay', () => {
        console.log("✅ Audio can play");
      });
      
      audio.addEventListener('playing', () => {
        console.log("▶️ Audio is playing");
        setConversationState("ai_speaking");
      });
      
      audio.addEventListener('ended', () => {
        console.log("✅ Audio playback completed - Your turn to speak");
        // Clear AI speaking flag when audio finishes
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        setConversationState("ready"); // Ready for next push-to-talk
        currentAudioRef.current = null;
      });
      
      audio.addEventListener('error', (e) => {
        console.error("❌ Audio playback error:", e);
        toast.error("Failed to play audio response");
        // Clear AI speaking flag on error
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        setConversationState("ready");
        currentAudioRef.current = null;
      });
      
      // Play with error handling
      try {
        await audio.play();
        console.log("🎵 Audio playback started successfully");
      } catch (playError) {
        console.error("❌ Failed to play audio:", playError);
        toast.error("Audio playback blocked. Please check browser permissions.");
        // Clear AI speaking flag on error
        setIsAiSpeaking(false);
        isAiSpeakingRef.current = false;
        setConversationState("ready");
        currentAudioRef.current = null;
      }
      
    } catch (error) {
      console.error("❌ Error processing audio:", error);
      toast.error(error.response?.data?.detail || "Error processing audio");
      // Clear AI speaking flag on error
      setIsAiSpeaking(false);
      isAiSpeakingRef.current = false;
      setConversationState("ready");
      // Continue call even if one iteration fails
    }
  };

  // Keyword-based Barge-In Detection using Web Speech API
  const startKeywordBargeInDetection = (ws, mode = "elevenlabs") => {
    try {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        console.warn("Speech Recognition not supported in this browser");
        return;
      }
      
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onresult = async (event) => {
        // Get the latest result with confidence score
        const results = Array.from(event.results);
        const latestResult = results[results.length - 1];
        const transcript = latestResult[0].transcript.toLowerCase();
        const confidence = latestResult[0].confidence;
        
        console.log(`🎤 Speech detected: "${transcript}" (Confidence: ${confidence ? confidence.toFixed(2) : 'N/A'}, Status: ${voiceAIStatusRef.current})`);
        
        // ONLY process speech if AI is speaking (for barge-in) or if we're ready/listening
        // Ignore speech detection at other times (prevents false triggers)
        const currentStatus = voiceAIStatusRef.current;
        if (currentStatus !== "ai_speaking" && currentStatus !== "ready" && currentStatus !== "listening") {
          console.log(`⚠️ Ignoring speech - invalid status: ${currentStatus}`);
          return;
        }
        
        // Check if AI is speaking based on status
        const aiIsSpeaking = currentStatus === "ai_speaking";
        
        // Check if any keyword is detected
        const keywords = bargeInKeywordsRef.current || [];
        const keywordDetected = keywords.some(keyword => transcript.includes(keyword.toLowerCase()));
        
        // For OpenAI mode: Add confidence threshold to filter out AI's voice
        // Only trigger if confidence is high enough (user speaking clearly)
        const MIN_CONFIDENCE = mode === "openai" ? 0.7 : 0.5; // Higher threshold for OpenAI
        const isConfidentMatch = !confidence || confidence >= MIN_CONFIDENCE;
        
        console.log(`🔍 Mode: ${mode}, Keywords: [${keywords.join(", ")}], Detected: ${keywordDetected}, Confident: ${isConfidentMatch}`);
        
        // ONLY trigger barge-in if keyword detected AND AI is speaking AND confidence is high enough
        if (keywordDetected && aiIsSpeaking && isConfidentMatch && !voiceAIBargeInActiveRef.current) {
          console.log(`🎯 Barge-in keyword detected: "${transcript}" (Confidence: ${confidence ? confidence.toFixed(2) : 'N/A'})`);
          
          
          // Trigger barge-in
          voiceAIBargeInActiveRef.current = true;
          
          // Stop current audio - AGGRESSIVE APPROACH
          console.log("🛑 Attempting to stop audio playback...");
          console.log(`📊 Current state: audioContext=${!!voiceAIAudioContextRef.current}, currentSource=${!!voiceAICurrentSourceRef.current}, isPlaying=${voiceAIIsPlayingRef.current}, queueSize=${voiceAIPlaybackQueueRef.current.length}`);
          
          // Method 1: Stop current audio source
          if (voiceAICurrentSourceRef.current) {
            try {
              voiceAICurrentSourceRef.current.stop();
              voiceAICurrentSourceRef.current.disconnect();
              voiceAICurrentSourceRef.current = null;
              console.log("✅ Stopped and disconnected current audio source");
            } catch (err) {
              console.log("⚠️ Audio source stop failed:", err.message);
            }
          } else {
            console.log("⚠️ No current audio source to stop");
          }
          
          // Method 2: Close PLAYBACK audio context (NOT capture context - that stays alive!)
          const oldPlaybackContext = voiceAIAudioContextRef.current;
          if (oldPlaybackContext) {
            console.log(`🔍 Playback context state before close: ${oldPlaybackContext.state}`);
            try {
              await oldPlaybackContext.close();
              voiceAIAudioContextRef.current = null;
              console.log("✅ Closed playback context - AI audio stopped, microphone capture still active");
            } catch (err) {
              console.error("❌ Could not close playback context:", err);
            }
          } else {
            console.log("⚠️ No playback context to close");
          }
          
          // Clear queue
          const queueSize = voiceAIPlaybackQueueRef.current.length;
          voiceAIPlaybackQueueRef.current = [];
          voiceAIIsPlayingRef.current = false;
          console.log(`✅ Cleared playback queue (${queueSize} items removed)`);
          
          // Send interrupt to backend to cancel current response generation
          try {
            ws.send(JSON.stringify({
              type: "interrupt"
            }));
            console.log("🛑 Sent interrupt to backend to cancel old response");
          } catch (err) {
            console.error("❌ Failed to send interrupt:", err);
          }
          
          // Keep barge-in flag active temporarily to block old audio chunks
          console.log("🎙️ Barge-in triggered - flag active, waiting for backend response");
          
          // Update status to listening (ready for user's new input)
          voiceAIStatusRef.current = "listening";
          setVoiceAIStatus("listening");
        }
      };
      
      recognition.onerror = (event) => {
        console.error("🚨 Speech recognition error:", event.error);
        
        // Restart recognition if it stops
        if (event.error === 'no-speech' || event.error === 'audio-capture') {
          console.log("🔄 Restarting speech recognition...");
          setTimeout(() => {
            if (voiceAISpeechRecognitionRef.current) {
              try {
                voiceAISpeechRecognitionRef.current.start();
              } catch (e) {
                console.log("Recognition already running");
              }
            }
          }, 1000);
        }
      };
      
      recognition.onend = () => {
        console.log("🎤 Speech recognition ended - restarting...");
        // Auto-restart recognition when it ends
        setTimeout(() => {
          if (voiceAISpeechRecognitionRef.current && voiceAIStatusRef.current !== "idle") {
            try {
              voiceAISpeechRecognitionRef.current.start();
              console.log("🎤 Speech recognition restarted");
            } catch (e) {
              console.log("Could not restart recognition:", e.message);
            }
          }
        }, 100);
      };
      
      recognition.start();
      voiceAISpeechRecognitionRef.current = recognition;
      console.log("🎤 Keyword barge-in detection started with keywords:", bargeInKeywordsRef.current);
    } catch (error) {
      console.error("Failed to start keyword barge-in:", error);
    }
  };
  // Voice AI Functions
  const startVoiceAI = async () => {
    if (!selectedVoiceAIActorId) {
      toast.error("Please select an AI Actor");
      return;
    }
    
    if (!selectedVoiceId) {
      toast.error("Please select a voice");
      return;
    }

    try {
      setVoiceAIStatus("connecting");
      console.log("🎙️ Voice AI - Starting...");
      
      // Request microphone access
      // Remove sampleRate constraint to let browser use its native rate
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      voiceAIStreamRef.current = stream;
      
      // Create SEPARATE AudioContexts for capture and playback
      // Capture context: For microphone input (never close during barge-in)
      // Use browser's default sample rate to avoid Firefox compatibility issues
      const captureContext = new (window.AudioContext || window.webkitAudioContext)();
      voiceAICaptureContextRef.current = captureContext;
      
      console.log(`🎤 Capture context created with sample rate: ${captureContext.sampleRate}Hz`);
      
      // Playback context: For AI audio output (can close/recreate for barge-in)
      const playbackContext = new (window.AudioContext || window.webkitAudioContext)();
      voiceAIAudioContextRef.current = playbackContext;
      
      console.log(`🔊 Playback context created with sample rate: ${playbackContext.sampleRate}Hz`);
      
      // Connect to WebSocket
      const wsUrl = `${BACKEND_URL.replace('https', 'wss').replace('http', 'ws')}/api/ws/voice-ai/${selectedVoiceAIActorId}?mode=${voiceAIMode}&openai_voice=${selectedOpenAIVoice}`;
      console.log("🔌 Voice AI - Connecting to:", wsUrl);
      console.log("🎙️ Voice AI - Mode:", voiceAIMode);
      
      const ws = new WebSocket(wsUrl);
      setVoiceAIWebSocket(ws);
      
      ws.onopen = () => {
        console.log("✅ Voice AI - WebSocket connected");
        voiceAIStatusRef.current = "connecting";
        setVoiceAIStatus("connecting");
      };
      
      ws.onmessage = async (event) => {
        if (event.data instanceof Blob) {
          // Audio data from backend
          const audioData = await event.data.arrayBuffer();
          
          // Don't queue audio if barge-in is active (user is interrupting)
          if (!voiceAIBargeInActiveRef.current) {
            voiceAIPlaybackQueueRef.current.push(audioData);
            
            if (!voiceAIIsPlayingRef.current) {
              playVoiceAIQueue();
            }
          } else {
            console.log("🔇 Dropping audio chunk during barge-in");
          }
        } else {
          // JSON message
          const message = JSON.parse(event.data);
          console.log("📨 Voice AI - Received message:", message.type);
          
          if (message.type === "ready") {
            voiceAIStatusRef.current = "ready";
            setVoiceAIStatus("ready");
            setVoiceAISessionId(message.session_id);
            
            // Store barge-in settings
            if (message.barge_in_enabled !== undefined) {
              setEnableTrueBargeIn(message.barge_in_enabled);
              enableTrueBargeInRef.current = message.barge_in_enabled;
              console.log(`🎯 Voice AI - Barge-in enabled: ${message.barge_in_enabled}`);
            }
            if (message.barge_in_mode) {
              setBargeInMode(message.barge_in_mode);
              bargeInModeRef.current = message.barge_in_mode;
              console.log(`🎯 Voice AI - Barge-in mode: ${message.barge_in_mode}`);
            }
            if (message.barge_in_keywords) {
              setBargeInKeywords(message.barge_in_keywords);
              bargeInKeywordsRef.current = message.barge_in_keywords;
              console.log(`🎯 Voice AI - Barge-in keywords: ${message.barge_in_keywords.join(", ")}`);
            }
            if (message.barge_in_message) {
              setBargeInMessage(message.barge_in_message);
              bargeInMessageRef.current = message.barge_in_message;
              console.log(`🎯 Voice AI - Barge-in message: "${message.barge_in_message}"`);
            }
            
            toast.success("Voice AI ready! Start speaking naturally...");
            
            // Start capturing and sending audio using CAPTURE context
            startVoiceAIAudioCapture(ws, stream, captureContext);
            
            // Start keyword-based barge-in detection for BOTH modes
            // Both OpenAI and ElevenLabs need it for user interruption during AI speech
            if (message.barge_in_enabled && message.barge_in_mode === "keyword") {
              startKeywordBargeInDetection(ws, message.mode);
              console.log(`🎤 Started keyword barge-in detection (${message.mode} mode)`);
            }
          } else if (message.type === "ai_text") {
            console.log("💬 Voice AI - AI said:", message.text);
            setVoiceAILog(prev => [...prev, { role: 'assistant', text: message.text }]);
            voiceAIStatusRef.current = "ai_speaking";
            setVoiceAIStatus("ai_speaking");
            
            // KEEP speech recognition running during AI speech to detect barge-in keywords
            // Speech recognition will detect keywords like "hold on", "wait", "stop" while AI speaks
            console.log("🎤 Speech recognition active - listening for barge-in keywords during AI speech");
            
            // Clear barge-in flag when new AI response starts (allow audio playback)
            voiceAIBargeInActiveRef.current = false;
          } else if (message.type === "audio_complete") {
            console.log("✅ Voice AI - Audio playback complete");
            
            // Don't change status immediately - audio might still be playing
            // Let the playback queue finish naturally
            console.log(`📊 Queue status: playing=${voiceAIIsPlayingRef.current}, queue=${voiceAIPlaybackQueueRef.current.length}`);
            
            // Only change to ready if:
            // 1. Not in barge-in
            // 2. Nothing is playing
            // 3. Queue is empty
            if (!voiceAIBargeInActiveRef.current && !voiceAIIsPlayingRef.current && voiceAIPlaybackQueueRef.current.length === 0) {
              voiceAIStatusRef.current = "ready";
              setVoiceAIStatus("ready");
              console.log("✅ Changed status to ready (no audio remaining)");
            } else {
              console.log("⏳ Keeping ai_speaking status (audio still playing/queued)");
            }
            
            // Clear barge-in flag when audio completes
            voiceAIBargeInActiveRef.current = false;
          } else if (message.type === "interrupted") {
            console.log("⚠️ Voice AI - Interrupted - ready for new input");
            voiceAIPlaybackQueueRef.current = [];
            
            // Clear barge-in flag immediately so new AI response can play
            voiceAIBargeInActiveRef.current = false;
            console.log("✅ Barge-in flag cleared - ready for new response audio");
            
            // Set status to "ready" so user can speak naturally
            voiceAIStatusRef.current = "ready";
            setVoiceAIStatus("ready");
          } else if (message.type === "user_transcript") {
            // User's speech transcript from backend
            console.log("👤 Voice AI - User said:", message.text);
            setVoiceAILog(prev => [...prev, { role: 'user', text: message.text }]);
          } else if (message.type === "kb_tracking") {
            // KB tracking information from backend
            console.log("📚 Voice AI - KB tracking:", message.kb_items);
            setKBTrackingInfo({
              lastQuery: "Voice AI query",
              kbItemsUsed: message.kb_items || [],
              threshold: 0.7,
              totalItems: message.total_items_used || 0,
              timestamp: new Date().toISOString()
            });
            setShowKBTracking(true); // Auto-show KB tracking when items are used
          } else if (message.type === "error") {
            console.error("❌ Voice AI - Error:", message.message);
            toast.error(message.message);
            stopVoiceAI(false);  // Don't show "stopped" toast when there's an error
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error("❌ Voice AI - WebSocket error:", error);
        toast.error("Voice AI connection error");
        stopVoiceAI(false);  // Don't show "stopped" toast when there's an error
      };
      
      ws.onclose = () => {
        console.log("🔌 Voice AI - WebSocket closed");
        setVoiceAIStatus("idle");
      };
      
    } catch (error) {
      console.error("❌ Voice AI - Failed to start:", error);
      toast.error("Failed to start Voice AI: " + error.message);
      setVoiceAIStatus("idle");
    }
  };

  const startVoiceAIAudioCapture = (ws, stream, audioContext) => {
    const source = audioContext.createMediaStreamSource(stream);
    const processor = audioContext.createScriptProcessor(4096, 1, 1);
    
    source.connect(processor);
    processor.connect(audioContext.destination);
    
    voiceAIProcessorRef.current = processor;
    
    // Calculate resampling ratio (browser sample rate → 16kHz)
    const targetSampleRate = 16000;
    const sourceSampleRate = audioContext.sampleRate;
    const ratio = sourceSampleRate / targetSampleRate;
    
    console.log(`🔄 Audio resampling: ${sourceSampleRate}Hz → ${targetSampleRate}Hz (ratio: ${ratio.toFixed(2)})`);
    
    processor.onaudioprocess = (e) => {
      if (ws.readyState === WebSocket.OPEN && voiceAIStatusRef.current !== "idle") {
        const inputData = e.inputBuffer.getChannelData(0);
        
        // Resample to 16kHz if needed
        let resampledData = inputData;
        if (ratio !== 1) {
          const newLength = Math.round(inputData.length / ratio);
          resampledData = new Float32Array(newLength);
          
          for (let i = 0; i < newLength; i++) {
            const srcIndex = i * ratio;
            const srcIndexInt = Math.floor(srcIndex);
            const srcIndexFrac = srcIndex - srcIndexInt;
            
            // Linear interpolation
            const sample1 = inputData[srcIndexInt] || 0;
            const sample2 = inputData[srcIndexInt + 1] || sample1;
            resampledData[i] = sample1 + (sample2 - sample1) * srcIndexFrac;
          }
        }
        
        // Convert Float32Array to Int16Array (PCM16)
        const pcm16 = new Int16Array(resampledData.length);
        for (let i = 0; i < resampledData.length; i++) {
          const s = Math.max(-1, Math.min(1, resampledData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        
        // Send as binary
        ws.send(pcm16.buffer);
        
        // Update status based on volume (for listening detection only)
        const volume = Math.sqrt(resampledData.reduce((sum, val) => sum + val * val, 0) / resampledData.length);
        
        // Volume-based barge-in (only if mode is "volume")
        if (enableTrueBargeInRef.current && bargeInModeRef.current === "volume") {
          const audioIsActive = voiceAIIsPlayingRef.current || voiceAIPlaybackQueueRef.current.length > 0;
          
          if (volume > 0.005 && audioIsActive && !voiceAIBargeInActiveRef.current) {
            console.log("🎙️ Volume-based Barge-In detected! Pausing AI audio...");
            
            // Trigger barge-in
            voiceAIBargeInActiveRef.current = true;
            
            // Stop current audio
            if (voiceAICurrentSourceRef.current) {
              try {
                voiceAICurrentSourceRef.current.stop();
                voiceAICurrentSourceRef.current = null;
              } catch (err) {
                console.log("Audio source already stopped");
              }
            }
            
            // Clear queue
            voiceAIPlaybackQueueRef.current = [];
            voiceAIIsPlayingRef.current = false;
            
            // Send acknowledgment
            const acknowledgment = bargeInMessageRef.current || "Yes, please go ahead.";
            ws.send(JSON.stringify({
              type: "barge_in_acknowledgment",
              message: acknowledgment
            }));
            
            // Update status
            voiceAIStatusRef.current = "listening";
            setVoiceAIStatus("listening");
          }
        }
        
        // Normal status update based on volume - ONLY if NOT already speaking
        // Don't change status if AI is speaking (prevents premature status changes)
        if (volume > 0.01 && voiceAIStatusRef.current === "ready") {
          voiceAIStatusRef.current = "listening";
          setVoiceAIStatus("listening");
        }
      }
    };
    
    source.connect(processor);
    processor.connect(audioContext.destination);
    
    console.log("🎤 Voice AI - Audio capture started");
  };

  const playVoiceAIQueue = async () => {
    if (voiceAIIsPlayingRef.current) return;
    
    voiceAIIsPlayingRef.current = true;
    
    while (voiceAIPlaybackQueueRef.current.length > 0 && !voiceAIBargeInActiveRef.current) {
      const audioData = voiceAIPlaybackQueueRef.current.shift();
      
      try {
        // Recreate PLAYBACK audio context if it was closed during barge-in
        let playbackContext = voiceAIAudioContextRef.current;
        if (!playbackContext || playbackContext.state === 'closed') {
          playbackContext = new AudioContext();
          voiceAIAudioContextRef.current = playbackContext;
          console.log(`🔊 Recreated playback context (${playbackContext.sampleRate}Hz) after barge-in`);
        }
        
        // Try to decode as a complete audio file (for ElevenLabs MP3/WAV)
        try {
          const audioBuffer = await playbackContext.decodeAudioData(audioData.slice(0));
          const source = playbackContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(playbackContext.destination);
          
          
          // Store reference for potential barge-in interruption
          voiceAICurrentSourceRef.current = source;
          
          await new Promise((resolve) => {
            source.onended = () => {
              voiceAICurrentSourceRef.current = null;
              resolve();
            };
            source.start(0);
          });
        } catch (decodeError) {
          // If decoding fails, treat as raw PCM16 (for OpenAI)
          console.log("🔧 Voice AI - Treating as raw PCM16 audio");
          
          // Convert ArrayBuffer to Int16Array
          const pcm16 = new Int16Array(audioData);
          
          // Create AudioBuffer for 16kHz mono PCM16
          const audioBuffer = playbackContext.createBuffer(1, pcm16.length, 24000); // OpenAI uses 24kHz
          const channelData = audioBuffer.getChannelData(0);
          
          // Convert Int16 to Float32 for Web Audio API
          for (let i = 0; i < pcm16.length; i++) {
            channelData[i] = pcm16[i] / 32768.0; // Convert to -1.0 to 1.0 range
          }
          
          const source = playbackContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(playbackContext.destination);
          
          
          // Store reference for potential barge-in interruption
          voiceAICurrentSourceRef.current = source;
          
          await new Promise((resolve) => {
            source.onended = () => {
              voiceAICurrentSourceRef.current = null;
              resolve();
            };
            source.start(0);
          });
        }
      } catch (error) {
        console.error("❌ Voice AI - Audio playback error:", error);
      }
    }
    
    voiceAIIsPlayingRef.current = false;
    
    // After playback finishes, change status to ready if still in ai_speaking
    if (voiceAIStatusRef.current === "ai_speaking") {
      voiceAIStatusRef.current = "ready";
      setVoiceAIStatus("ready");
      console.log("✅ Playback complete - changed status to ready");
    }
  };

  const interruptVoiceAI = () => {
    if (voiceAIWebSocket && voiceAIWebSocket.readyState === WebSocket.OPEN) {
      console.log("🛑 Voice AI - Interrupting...");
      voiceAIWebSocket.send(JSON.stringify({ type: "interrupt" }));
      voiceAIPlaybackQueueRef.current = [];
      setVoiceAIStatus("ready");
      toast.info("Interrupted");
    }
  };

  const stopVoiceAI = (showToast = true) => {
    console.log("🛑 Voice AI - Stopping...");
    
    // Stop speech recognition
    if (voiceAISpeechRecognitionRef.current) {
      voiceAISpeechRecognitionRef.current.stop();
      voiceAISpeechRecognitionRef.current = null;
      console.log("🎤 Keyword barge-in detection stopped");
    }
    
    // Close WebSocket
    if (voiceAIWebSocket) {
      voiceAIWebSocket.close();
      setVoiceAIWebSocket(null);
    }
    
    // Stop audio capture
    if (voiceAIProcessorRef.current) {
      voiceAIProcessorRef.current.disconnect();
      voiceAIProcessorRef.current = null;
    }
    
    // Stop microphone
    if (voiceAIStreamRef.current) {
      voiceAIStreamRef.current.getTracks().forEach(track => track.stop());
      voiceAIStreamRef.current = null;
    }
    
    // Close audio contexts (both capture and playback)
    if (voiceAICaptureContextRef.current) {
      voiceAICaptureContextRef.current.close();
      voiceAICaptureContextRef.current = null;
      console.log("🔇 Closed capture audio context");
    }
    
    if (voiceAIAudioContextRef.current) {
      voiceAIAudioContextRef.current.close();
      voiceAIAudioContextRef.current = null;
      console.log("🔇 Closed playback audio context");
    }
    
    // Clear playback queue
    voiceAIPlaybackQueueRef.current = [];
    voiceAIIsPlayingRef.current = false;
    
    voiceAIStatusRef.current = "idle";
    setVoiceAIStatus("idle");
    setVoiceAILog([]);
    
    // Only show toast if explicitly requested (not shown when there's an error)
    if (showToast) {
      toast.info("Voice AI stopped");
    }
  };

  // Fetch actor's barge-in setting
  const fetchActorBargeInSetting = async (actorId) => {
    if (!actorId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.get(`${API}/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const actor = response.data;
      // Barge-in setting is a top-level field on the actor
      const bargeInEnabled = actor?.enable_true_barge_in ?? false;
      setEnableTrueBargeIn(bargeInEnabled);
      enableTrueBargeInRef.current = bargeInEnabled;  // Update ref for use in closures
      console.log(`🎙️ Voice AI - Barge-in setting for actor: ${bargeInEnabled}`);
    } catch (error) {
      console.error("Failed to fetch actor barge-in setting:", error);
      // Default to false if fetch fails
      setEnableTrueBargeIn(false);
      enableTrueBargeInRef.current = false;
    }
  };
  
  // Handler for Voice AI actor selection
  const handleVoiceAIActorChange = (actorId) => {
    setSelectedVoiceAIActorId(actorId);
    fetchActorBargeInSetting(actorId);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with Settings Button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Voice Models</h1>
          <p className="text-muted-foreground">Manage Text-to-Speech and Conversational AI</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/ai/voice-model-settings')}>
          <Settings className="w-4 h-4 mr-2" />
          Voice Settings
        </Button>
      </div>

      {/* Tabs for TTS, Conversational AI, and Voice AI */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="tts" className="flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            Text to Speech
          </TabsTrigger>
          <TabsTrigger value="conversational" className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Conversational AI
          </TabsTrigger>
          <TabsTrigger value="voice_ai" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Voice AI
          </TabsTrigger>
        </TabsList>

        {/* Text to Speech Tab */}
        <TabsContent value="tts" className="space-y-4 mt-6">
      {/* Text to Speech Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-violet-600" />
                Text to Speech
              </CardTitle>
              <CardDescription>
                Generate speech from text using ElevenLabs voices
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchCurrentVoiceInfo}
              disabled={loadingVoiceInfo}
            >
              {loadingVoiceInfo ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Text Input */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="tts-text" className="text-sm font-medium">Text</Label>
              <span className="text-xs text-muted-foreground">{ttsText.length} characters</span>
            </div>
            <Textarea
              id="tts-text"
              value={ttsText}
              onChange={(e) => setTtsText(e.target.value)}
              placeholder="Type or paste the text you want to convert to speech..."
              className="min-h-[120px] resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column - Settings */}
            <div className="space-y-4">
              {/* Voice Selection */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-medium">Voice</Label>
                  {selectedVoiceId && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={previewVoice}
                      disabled={previewingVoice}
                    >
                      {previewingVoice ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          <span className="text-xs">Previewing...</span>
                        </>
                      ) : (
                        <>
                          <Play className="w-3 h-3 mr-1" />
                          <span className="text-xs">Preview</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
                <Select value={selectedVoiceId} onValueChange={setSelectedVoiceId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {/* Your Cloned Voices */}
                    {groupedVoices.cloned.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100">
                          Your Voices ({groupedVoices.cloned.length})
                        </div>
                        {groupedVoices.cloned.map((voice) => (
                          <SelectItem key={voice.voice_id} value={voice.voice_id}>
                            <div className="flex items-center gap-2">
                              <span>⭐</span>
                              <span>{voice.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    
                    {/* Professional Voices */}
                    {groupedVoices.professional.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                          Professional Voices ({groupedVoices.professional.length})
                        </div>
                        {groupedVoices.professional.map((voice) => (
                          <SelectItem key={voice.voice_id} value={voice.voice_id}>
                            <div className="flex items-center gap-2">
                              <span>🎙️</span>
                              <span>{voice.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                    
                    {/* Premade Voices */}
                    {groupedVoices.premade.length > 0 && (
                      <>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                          Premade Voices ({groupedVoices.premade.length})
                        </div>
                        {groupedVoices.premade.map((voice) => (
                          <SelectItem key={voice.voice_id} value={voice.voice_id}>
                            <div className="flex items-center gap-2">
                              <span>🔊</span>
                              <span>{voice.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {selectedVoiceId && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {availableVoices.find(v => v.voice_id === selectedVoiceId)?.category === "cloned" && (
                      <Badge variant="secondary" className="text-xs">Your Voice</Badge>
                    )}
                    {availableVoices.find(v => v.voice_id === selectedVoiceId)?.category === "professional" && (
                      <Badge variant="outline" className="text-xs">Professional</Badge>
                    )}
                    {availableVoices.find(v => v.voice_id === selectedVoiceId)?.category === "premade" && (
                      <Badge variant="outline" className="text-xs">Premade</Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Model Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Model</Label>
                <Select value={selectedModel} onValueChange={handleModelChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model) => (
                      <SelectItem key={model.model_id} value={model.model_id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedModelData && (
                  <p className="text-xs text-muted-foreground">{selectedModelData.description}</p>
                )}
              </div>

              {/* Generate Button */}
              <Button 
                onClick={generateSpeech} 
                disabled={generatingSpeech || !ttsText.trim() || !selectedVoiceId}
                className="w-full"
                size="lg"
              >
                {generatingSpeech ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>

              {/* Assign to AI Actor Button */}
              <Button 
                onClick={() => setShowAssignDialog(true)} 
                disabled={!selectedVoiceId}
                variant="outline"
                className="w-full"
              >
                <Settings className="w-4 h-4 mr-2" />
                Assign to AI Actor
              </Button>
            </div>

            {/* Right Column - Voice Settings */}
            <div className="space-y-4">
              <Label className="text-sm font-medium">Voice Settings</Label>
              
              {/* Stability */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs text-muted-foreground">Stability</Label>
                  <span className="text-xs font-mono">{stability.toFixed(2)}</span>
                </div>
                <Slider
                  value={[stability]}
                  onValueChange={(value) => setStability(value[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  className="w-full"
                />
              </div>

              {/* Similarity */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs text-muted-foreground">Similarity</Label>
                  <span className="text-xs font-mono">{similarity.toFixed(2)}</span>
                </div>
                <Slider
                  value={[similarity]}
                  onValueChange={(value) => setSimilarity(value[0])}
                  min={0}
                  max={1}
                  step={0.01}
                  className="w-full"
                />
              </div>

              {/* Speed */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs text-muted-foreground">Speed</Label>
                  <span className="text-xs font-mono">{speed.toFixed(2)}x</span>
                </div>
                <Slider
                  value={[speed]}
                  onValueChange={(value) => setSpeed(value[0])}
                  min={0.25}
                  max={4.0}
                  step={0.01}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">0.25x (slower) to 4.0x (faster)</p>
              </div>

              {/* Style Exaggeration - Conditional */}
              {selectedModelData?.can_use_style && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs text-muted-foreground">Style Exaggeration</Label>
                    <span className="text-xs font-mono">{style.toFixed(2)}</span>
                  </div>
                  <Slider
                    value={[style]}
                    onValueChange={(value) => setStyle(value[0])}
                    min={0}
                    max={1}
                    step={0.01}
                    className="w-full"
                  />
                </div>
              )}

              {/* Speaker Boost - Conditional */}
              {selectedModelData?.can_use_speaker_boost && (
                <div className="flex items-center justify-between py-2">
                  <Label className="text-xs text-muted-foreground">Speaker Boost</Label>
                  <Button
                    variant={speakerBoost ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSpeakerBoost(!speakerBoost)}
                  >
                    {speakerBoost ? "On" : "Off"}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Generated Audio */}
          {generatedAudioUrl && (
            <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3 mt-4">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-green-800">Speech generated successfully!</span>
              </div>
              <audio 
                controls 
                src={generatedAudioUrl} 
                className="w-full"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(generatedAudioUrl, '_blank')}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setGeneratedAudioUrl(null);
                    setTtsText("");
                  }}
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-2" />
                  Clear
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Conversational AI Tab */}
        <TabsContent value="conversational" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-600" />
                Conversational AI
              </CardTitle>
              <CardDescription>
                Real-time voice conversation with AI Actor using WebRTC
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Voice Configuration - Same as TTS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Left Column - Settings */}
                <div className="space-y-4">
                  {/* Voice Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Voice</Label>
                    <Select value={selectedVoiceId} onValueChange={setSelectedVoiceId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a voice" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {groupedVoices.cloned.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100">
                              Your Voices ({groupedVoices.cloned.length})
                            </div>
                            {groupedVoices.cloned.map((voice) => (
                              <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                <div className="flex items-center gap-2">
                                  <span>⭐</span>
                                  <span>{voice.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {groupedVoices.professional.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                              Professional Voices ({groupedVoices.professional.length})
                            </div>
                            {groupedVoices.professional.map((voice) => (
                              <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                <div className="flex items-center gap-2">
                                  <span>🎙️</span>
                                  <span>{voice.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                        {groupedVoices.premade.length > 0 && (
                          <>
                            <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                              Premade Voices ({groupedVoices.premade.length})
                            </div>
                            {groupedVoices.premade.map((voice) => (
                              <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                <div className="flex items-center gap-2">
                                  <span>🔊</span>
                                  <span>{voice.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Model Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Model</Label>
                    <Select value={selectedModel} onValueChange={handleModelChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableModels.map((model) => (
                          <SelectItem key={model.model_id} value={model.model_id}>
                            {model.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Whisper Model Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Speech Recognition Model</Label>
                    <Select value={selectedWhisperModel} onValueChange={setSelectedWhisperModel}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {whisperModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center justify-between w-full gap-2">
                              <span>{model.name}</span>
                              {model.tag && (
                                <Badge variant="secondary" className="text-xs">
                                  {model.tag}
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {whisperModels.find(m => m.id === selectedWhisperModel)?.description && (
                      <p className="text-xs text-muted-foreground">
                        {whisperModels.find(m => m.id === selectedWhisperModel)?.description}
                      </p>
                    )}
                  </div>

                  {/* AI Actor Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">AI Actor</Label>
                    <Select value={selectedCallActorId} onValueChange={setSelectedCallActorId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an AI Actor" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiActors.map((actor) => (
                          <SelectItem key={actor.id} value={actor.id}>
                            {actor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The AI Actor's knowledge base, memory, and settings will be used for the conversation
                    </p>
                  </div>
                </div>

                {/* Right Column - Voice Parameters */}
                <div className="space-y-4">
                  {/* Stability */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium">Stability</Label>
                      <span className="text-xs text-muted-foreground">{stability.toFixed(2)}</span>
                    </div>
                    <Slider
                      value={[stability]}
                      onValueChange={(value) => setStability(value[0])}
                      min={0}
                      max={1}
                      step={0.01}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Higher values = more stable/consistent voice</p>
                  </div>

                  {/* Similarity */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium">Similarity</Label>
                      <span className="text-xs text-muted-foreground">{similarity.toFixed(2)}</span>
                    </div>
                    <Slider
                      value={[similarity]}
                      onValueChange={(value) => setSimilarity(value[0])}
                      min={0}
                      max={1}
                      step={0.01}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Higher values = closer to original voice</p>
                  </div>

                  {/* Speed */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium">Speed</Label>
                      <span className="text-xs text-muted-foreground">{speed.toFixed(2)}x</span>
                    </div>
                    <Slider
                      value={[speed]}
                      onValueChange={(value) => setSpeed(value[0])}
                      min={0.25}
                      max={4.0}
                      step={0.05}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Adjust speech speed (0.25x to 4.0x)</p>
                  </div>

                  {/* Style Exaggeration */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-sm font-medium">Style Exaggeration</Label>
                      <span className="text-xs text-muted-foreground">{style.toFixed(2)}</span>
                    </div>
                    <Slider
                      value={[style]}
                      onValueChange={(value) => setStyle(value[0])}
                      min={0}
                      max={1}
                      step={0.01}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">Enhance voice expressiveness</p>
                  </div>

                  {/* Speaker Boost */}
                  <div className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <Label className="text-sm font-medium">Speaker Boost</Label>
                      <p className="text-xs text-muted-foreground">Enhance clarity and quality</p>
                    </div>
                    <Button
                      variant={speakerBoost ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSpeakerBoost(!speakerBoost)}
                    >
                      {speakerBoost ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Call Controls */}
              <div className="border-t pt-6">
                <div className="flex flex-col items-center gap-4">
                  {!isCallActive ? (
                    <Button
                      size="lg"
                      onClick={startCall}
                      disabled={!selectedCallActorId || !selectedVoiceId}
                      className="w-48"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Start Call
                    </Button>
                  ) : (
                    <div className="flex flex-col items-center gap-4 w-full">
                      {/* Push to Talk Button */}
                      <Button
                        size="lg"
                        variant={isRecording ? "destructive" : "default"}
                        onMouseDown={startPushToTalk}
                        onMouseUp={stopPushToTalk}
                        onMouseLeave={stopPushToTalk}
                        onTouchStart={startPushToTalk}
                        onTouchEnd={stopPushToTalk}
                        disabled={isAiSpeaking || isProcessingRef.current}
                        className="w-64 h-20 text-lg font-semibold"
                      >
                        {isRecording ? (
                          <>
                            <MicOff className="w-6 h-6 mr-3 animate-pulse" />
                            Release to Send
                          </>
                        ) : (
                          <>
                            <Mic className="w-6 h-6 mr-3" />
                            Hold to Talk
                          </>
                        )}
                      </Button>
                      
                      <p className="text-xs text-muted-foreground text-center max-w-md">
                        {isRecording 
                          ? "🎤 Recording... Release button when done speaking" 
                          : "Hold the button and speak, then release to send your message"
                        }
                      </p>
                      
                      {/* End Call Button */}
                      <Button
                        variant="outline"
                        onClick={endCall}
                        className="w-32"
                      >
                        <PhoneOff className="w-4 h-4 mr-2" />
                        End Call
                      </Button>
                    </div>
                  )}

                  {/* Call Status */}
                  {callStatus && (
                    <div className="flex flex-col items-center gap-3 mt-4">
                      {callStatus === "connecting" && (
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                          <span className="text-sm text-muted-foreground">Connecting...</span>
                        </div>
                      )}
                      {callStatus === "connected" && (
                        <>
                          <div className="flex items-center gap-2">
                            {conversationState === "ready" && (
                              <>
                                <div className="w-3 h-3 bg-green-500 rounded-full" />
                                <span className="text-sm font-medium text-green-600">✅ Ready - Hold button to speak</span>
                              </>
                            )}
                            {conversationState === "recording" && (
                              <>
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                <span className="text-sm font-medium text-red-600">🎤 Recording - Release when done</span>
                              </>
                            )}
                            {conversationState === "processing" && (
                              <>
                                <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                                <span className="text-sm font-medium text-blue-600">⚙️ Processing your message...</span>
                              </>
                            )}
                            {conversationState === "ai_speaking" && (
                              <>
                                <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
                                <span className="text-sm font-medium text-orange-600">🔊 AI is speaking</span>
                              </>
                            )}
                          </div>
                          
                          {/* Visual Turn Indicator */}
                          <div className="flex items-center gap-4 px-4 py-2 bg-slate-100 rounded-lg">
                            <div className={`flex items-center gap-2 px-3 py-1 rounded ${conversationState === "ready" || conversationState === "recording" ? "bg-green-200" : "bg-gray-200"}`}>
                              <Mic className={`w-4 h-4 ${conversationState === "ready" || conversationState === "recording" ? "text-green-700" : "text-gray-400"}`} />
                              <span className={`text-xs font-medium ${conversationState === "ready" || conversationState === "recording" ? "text-green-700" : "text-gray-400"}`}>
                                You
                              </span>
                            </div>
                            <div className={`flex items-center gap-2 px-3 py-1 rounded ${conversationState === "ai_speaking" ? "bg-orange-200" : "bg-gray-200"}`}>
                              <Volume2 className={`w-4 h-4 ${conversationState === "ai_speaking" ? "text-orange-700" : "text-gray-400"}`} />
                              <span className={`text-xs font-medium ${conversationState === "ai_speaking" ? "text-orange-700" : "text-gray-400"}`}>
                                AI Actor
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                      {callStatus === "disconnected" && (
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-gray-400 rounded-full" />
                          <span className="text-sm text-muted-foreground">Call Ended</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Conversation Log */}
              {conversationLog.length > 0 && (
                <div className="border-t pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    <h3 className="font-semibold">Conversation Log</h3>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto p-4 bg-slate-50 rounded-lg">
                    {conversationLog.map((entry, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${
                          entry.role === 'user'
                            ? 'bg-blue-100 ml-8'
                            : 'bg-green-100 mr-8'
                        }`}
                      >
                        <p className="text-xs font-semibold mb-1">
                          {entry.role === 'user' ? 'You' : 'AI Actor'}
                        </p>
                        <p className="text-sm">{entry.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Base Source Tracking (Conversational AI) */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="font-semibold">Knowledge Base Tracking</h3>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowKBTracking(!showKBTracking)}
                    className="text-xs"
                  >
                    {showKBTracking ? "Hide" : "Show"} Details
                  </Button>
                </div>
                
                {showKBTracking ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-xs text-blue-800 mb-3">
                        <strong>ℹ️ About KB Tracking:</strong> Conversational AI uses semantic search to retrieve the top 8-10 most relevant Knowledge Base items. 
                        Check backend logs for detailed similarity scores and retrieval information.
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="font-semibold text-blue-900">Search Mode:</span>
                          <p className="text-blue-700">Semantic Search with Embeddings</p>
                        </div>
                        <div>
                          <span className="font-semibold text-blue-900">Top K Results:</span>
                          <p className="text-blue-700">10 items (configurable)</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-lg p-4">
                      <p className="text-sm font-semibold text-slate-700 mb-3">📊 How to View KB Tracking:</p>
                      <ol className="space-y-2 text-xs text-slate-600">
                        <li className="flex gap-2">
                          <span className="font-semibold">1.</span>
                          <span>Send a message using Conversational AI above</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-semibold">2.</span>
                          <span>Check browser console (F12) for "Conversational AI - Retrieved X KB results" logs</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-semibold">3.</span>
                          <span>Or check backend logs for "Semantic Search - Found X KB items" with similarity scores</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-semibold">4.</span>
                          <span>Each KB result includes: source_name, content preview, and similarity score (0.0-1.0)</span>
                        </li>
                      </ol>
                      
                      <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded">
                        <p className="text-xs text-emerald-800">
                          <strong>💡 Tip:</strong> Configure the similarity threshold in Edit Actor → Knowledge Base Configuration 
                          to control how relevant items must be (0 = no filter, 0.7 = moderate, 1.0 = very strict)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    Click "Show Details" to learn how to track Knowledge Base usage in Conversational AI.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voice AI Tab */}
        <TabsContent value="voice_ai" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                Voice AI - Real-time Conversation
              </CardTitle>
              <CardDescription>
                Natural voice conversation using OpenAI Realtime + ElevenLabs streaming TTS
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Voice Selection */}
              <div className="space-y-4">
                {/* Voice Mode Selector */}
                <div className="space-y-3 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <Label className="text-sm font-semibold text-purple-900">Voice Mode</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="voiceMode"
                        value="openai"
                        checked={voiceAIMode === "openai"}
                        onChange={(e) => setVoiceAIMode(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm font-medium">
                        ⚡ OpenAI Voice (Fast, Low Latency)
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="voiceMode"
                        value="elevenlabs"
                        checked={voiceAIMode === "elevenlabs"}
                        onChange={(e) => setVoiceAIMode(e.target.value)}
                        className="w-4 h-4 text-purple-600"
                      />
                      <span className="text-sm font-medium">
                        🎙️ ElevenLabs Voice (Custom Clone)
                      </span>
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {voiceAIMode === "openai" 
                      ? "Uses OpenAI's built-in voices for near real-time response"
                      : "Uses your custom ElevenLabs voice clone (may have higher latency)"
                    }
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* OpenAI Voice Selection (when OpenAI mode is selected) */}
                  {voiceAIMode === "openai" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">OpenAI Voice</Label>
                      <Select value={selectedOpenAIVoice} onValueChange={setSelectedOpenAIVoice}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select OpenAI voice" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alloy">Alloy (Neutral)</SelectItem>
                          <SelectItem value="echo">Echo (Male)</SelectItem>
                          <SelectItem value="fable">Fable (British Male)</SelectItem>
                          <SelectItem value="onyx">Onyx (Deep Male)</SelectItem>
                          <SelectItem value="nova">Nova (Female)</SelectItem>
                          <SelectItem value="shimmer">Shimmer (Soft Female)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Voice Selection (ElevenLabs - only when ElevenLabs mode is selected) */}
                  {voiceAIMode === "elevenlabs" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">ElevenLabs Voice</Label>
                      <Select value={selectedVoiceId} onValueChange={setSelectedVoiceId}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a voice" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {availableVoices.length === 0 ? (
                            <SelectItem value="loading" disabled>Loading voices...</SelectItem>
                          ) : (
                            <>
                              {groupedVoices.cloned.length > 0 && (
                                <>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100">
                                    Your Voices ({groupedVoices.cloned.length})
                                  </div>
                                  {groupedVoices.cloned.map((voice) => (
                                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                      <div className="flex items-center gap-2">
                                        <span>⭐</span>
                                        <span>{voice.name}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                              {groupedVoices.professional.length > 0 && (
                                <>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                                    Professional Voices ({groupedVoices.professional.length})
                                  </div>
                                  {groupedVoices.professional.map((voice) => (
                                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                      <div className="flex items-center gap-2">
                                        <span>🎙️</span>
                                        <span>{voice.name}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                              {groupedVoices.premade.length > 0 && (
                                <>
                                  <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                                    Premade Voices ({groupedVoices.premade.length})
                                  </div>
                                  {groupedVoices.premade.map((voice) => (
                                    <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                      <div className="flex items-center gap-2">
                                        <span>🔊</span>
                                        <span>{voice.name}</span>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </>
                              )}
                              {/* Fallback: Show all voices if no groups */}
                              {groupedVoices.cloned.length === 0 && groupedVoices.professional.length === 0 && groupedVoices.premade.length === 0 && availableVoices.map((voice) => (
                                <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                  <div className="flex items-center gap-2">
                                    <span>🔊</span>
                                    <span>{voice.name}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* AI Actor Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">AI Actor</Label>
                    <Select value={selectedVoiceAIActorId} onValueChange={handleVoiceAIActorChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an AI Actor" />
                      </SelectTrigger>
                      <SelectContent>
                        {aiActors.map((actor) => (
                          <SelectItem key={actor.id} value={actor.id}>
                            {actor.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Full AI Actor profile including KB, guard rails, and coaching notes will be used
                    </p>
                  </div>
                </div>
              </div>

              {/* True Barge-In Status Indicator */}
              {enableTrueBargeIn && voiceAIStatus !== "idle" && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-cyan-700">
                      {bargeInMode === "keyword" ? "Keyword Barge-In Active" : "True Barge-In Active"}
                    </span>
                  </div>
                  {bargeInMode === "keyword" && bargeInKeywords.length > 0 && (
                    <div className="text-xs text-cyan-600 text-center">
                      Say: <span className="font-semibold">{bargeInKeywords.slice(0, 3).join(", ")}</span>
                      {bargeInKeywords.length > 3 && ` (+${bargeInKeywords.length - 3} more)`}
                    </div>
                  )}
                </div>
              )}

              {/* Controls */}
              <div className="border-t pt-6">
                <div className="flex flex-col items-center gap-4">
                  {voiceAIStatus === "idle" ? (
                    <Button
                      size="lg"
                      onClick={startVoiceAI}
                      disabled={!selectedVoiceAIActorId || (voiceAIMode === "elevenlabs" && !selectedVoiceId)}
                      className="w-48"
                    >
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Start Voice AI
                    </Button>
                  ) : (
                    <div className="flex flex-col items-center gap-4 w-full">
                      <div className="flex items-center gap-4">
                        <Button
                          size="lg"
                          variant="outline"
                          onClick={interruptVoiceAI}
                          disabled={voiceAIStatus === "idle" || voiceAIStatus === "connecting"}
                        >
                          <AlertCircle className="w-5 h-5 mr-2" />
                          Interrupt
                        </Button>
                        <Button
                          size="lg"
                          variant="destructive"
                          onClick={stopVoiceAI}
                        >
                          <PhoneOff className="w-5 h-5 mr-2" />
                          Stop
                        </Button>
                      </div>
                      
                      <p className="text-xs text-muted-foreground text-center max-w-md">
                        {voiceAIStatus === "connecting" && "🔌 Connecting to Voice AI..."}
                        {voiceAIStatus === "ready" && "✅ Ready - Speak naturally, I'm listening"}
                        {voiceAIStatus === "listening" && "🎤 Listening to your voice..."}
                        {voiceAIStatus === "ai_speaking" && "🔊 AI is speaking..."}
                      </p>
                    </div>
                  )}

                  {/* Status Indicator */}
                  {voiceAIStatus !== "idle" && (
                    <div className="flex flex-col items-center gap-3 mt-4">
                      <div className="flex items-center gap-2">
                        {voiceAIStatus === "connecting" && (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            <span className="text-sm text-muted-foreground">Connecting...</span>
                          </>
                        )}
                        {voiceAIStatus === "ready" && (
                          <>
                            <div className="w-3 h-3 bg-green-500 rounded-full" />
                            <span className="text-sm font-medium text-green-600">Ready - Just speak naturally</span>
                          </>
                        )}
                        {voiceAIStatus === "listening" && (
                          <>
                            <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                            <span className="text-sm font-medium text-blue-600">Listening...</span>
                          </>
                        )}
                        {voiceAIStatus === "ai_speaking" && (
                          <>
                            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
                            <span className="text-sm font-medium text-orange-600">AI Speaking</span>
                          </>
                        )}
                      </div>
                      
                      {/* Visual Turn Indicator */}
                      <div className="flex items-center gap-4 px-4 py-2 bg-slate-100 rounded-lg">
                        <div className={`flex items-center gap-2 px-3 py-1 rounded ${voiceAIStatus === "listening" || voiceAIStatus === "ready" ? "bg-green-200" : "bg-gray-200"}`}>
                          <Mic className={`w-4 h-4 ${voiceAIStatus === "listening" || voiceAIStatus === "ready" ? "text-green-700" : "text-gray-400"}`} />
                          <span className={`text-xs font-medium ${voiceAIStatus === "listening" || voiceAIStatus === "ready" ? "text-green-700" : "text-gray-400"}`}>
                            You
                          </span>
                        </div>
                        <div className={`flex items-center gap-2 px-3 py-1 rounded ${voiceAIStatus === "ai_speaking" ? "bg-orange-200" : "bg-gray-200"}`}>
                          <Volume2 className={`w-4 h-4 ${voiceAIStatus === "ai_speaking" ? "text-orange-700" : "text-gray-400"}`} />
                          <span className={`text-xs font-medium ${voiceAIStatus === "ai_speaking" ? "text-orange-700" : "text-gray-400"}`}>
                            AI Actor
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Conversation Log */}
              {voiceAILog.length > 0 && (
                <div className="border-t pt-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                    <h3 className="font-semibold">Conversation Transcript</h3>
                  </div>
                  <div className="space-y-3 max-h-64 overflow-y-auto p-4 bg-slate-50 rounded-lg">
                    {voiceAILog.map((entry, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${
                          entry.role === 'user' 
                            ? 'bg-blue-100 ml-8' 
                            : 'bg-green-100 mr-8'
                        }`}
                      >
                        <p className="text-xs font-semibold mb-1">
                          {entry.role === 'user' ? 'You' : 'AI Actor'}
                        </p>
                        <p className="text-sm">{entry.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Knowledge Base Source Tracking */}
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="font-semibold">Knowledge Base Tracking</h3>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowKBTracking(!showKBTracking)}
                    className="text-xs"
                  >
                    {showKBTracking ? "Hide" : "Show"} Details
                  </Button>
                </div>
                
                {showKBTracking ? (
                  <div className="space-y-4">
                    {/* Info Box */}
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <p className="text-xs text-emerald-800 mb-3">
                        <strong>ℹ️ About KB Tracking:</strong> This section shows which Knowledge Base items are being used to answer questions. 
                        The system uses semantic search to find the most relevant content based on similarity scores.
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <span className="font-semibold text-emerald-900">Similarity Threshold:</span>
                          <p className="text-emerald-700">
                            {kbTrackingInfo.threshold === 0 ? "No filter (returns top K)" : `${(kbTrackingInfo.threshold * 100).toFixed(0)}% relevance required`}
                          </p>
                        </div>
                        <div>
                          <span className="font-semibold text-emerald-900">KB Items Available:</span>
                          <p className="text-emerald-700">{kbTrackingInfo.totalItems || "Loading..."}</p>
                        </div>
                      </div>
                    </div>

                    {/* Last Query Info */}
                    {kbTrackingInfo.lastQuery && (
                      <div className="bg-white border border-slate-200 rounded-lg p-4">
                        <p className="text-xs font-semibold text-slate-700 mb-2">Last Question Analyzed:</p>
                        <p className="text-sm text-slate-600 mb-3 italic">"{kbTrackingInfo.lastQuery}"</p>
                        
                        {kbTrackingInfo.kbItemsUsed.length > 0 ? (
                          <div className="space-y-3">
                            <p className="text-xs font-semibold text-slate-700">
                              Top {kbTrackingInfo.kbItemsUsed.length} Relevant KB Items Used:
                            </p>
                            {kbTrackingInfo.kbItemsUsed.map((item, index) => (
                              <div key={index} className="border border-emerald-200 bg-emerald-50 rounded p-3">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs bg-emerald-100 border-emerald-300">
                                      #{index + 1}
                                    </Badge>
                                    <span className="text-xs font-semibold text-emerald-900">{item.source_name}</span>
                                  </div>
                                  <Badge variant="secondary" className="text-xs">
                                    Relevance: {((item.similarity_score || item.similarity || 0) * 100).toFixed(1)}%
                                  </Badge>
                                </div>
                                <div className="text-xs text-emerald-800 mb-1">
                                  <strong>KB:</strong> {item.kb_name || 'Unknown'}
                                </div>
                                <p className="text-xs text-slate-600 line-clamp-2">
                                  {(item.content_preview || item.content || '').substring(0, 150)}...
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-sm text-slate-500">
                            No KB items matched the similarity threshold. Try lowering the threshold in Edit Actor settings.
                          </div>
                        )}
                        
                        {kbTrackingInfo.timestamp && (
                          <p className="text-xs text-slate-400 mt-3">
                            Updated: {new Date(kbTrackingInfo.timestamp).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}

                    {!kbTrackingInfo.lastQuery && (
                      <div className="text-center py-8 text-sm text-slate-500">
                        <p className="mb-2">📊 Waiting for conversation data...</p>
                        <p className="text-xs">Start a Voice AI call to see KB tracking information</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">
                    Click "Show Details" to see which Knowledge Base items are being used in responses.
                  </p>
                )}
              </div>
              
              {/* Features Info */}
              <div className="border-t pt-6">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-semibold text-purple-900 mb-2">✨ Voice AI Features:</h4>
                  <ul className="space-y-1 text-sm text-purple-800">
                    <li>🎤 Real-time voice conversation (no push-to-talk needed)</li>
                    <li>🧠 Full AI Actor context (knowledge base, guard rails, coaching)</li>
                    <li>⚡ Ultra-low latency streaming TTS</li>
                    <li>🛑 Barge-in support (interrupt AI anytime)</li>
                    <li>💬 Natural turn-taking with voice activity detection</li>
                    <li>💾 Conversations saved to database</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assign to AI Actor Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Voice to AI Actor</DialogTitle>
            <DialogDescription>
              Assign the current voice settings to an AI Actor. The actor will use these settings for text-to-speech generation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select AI Actor</Label>
              <Select value={selectedActorId} onValueChange={setSelectedActorId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an AI Actor" />
                </SelectTrigger>
                <SelectContent>
                  {aiActors.map((actor) => (
                    <SelectItem key={actor.id} value={actor.id}>
                      {actor.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
              <p className="text-sm font-semibold text-blue-900">Voice Settings to Assign:</p>
              <div className="text-xs text-blue-800 space-y-1">
                <p><strong>Voice:</strong> {availableVoices.find(v => v.voice_id === selectedVoiceId)?.name || "Not selected"}</p>
                <p><strong>Model:</strong> {selectedModel}</p>
                <p><strong>Stability:</strong> {stability.toFixed(2)}</p>
                <p><strong>Similarity:</strong> {similarity.toFixed(2)}</p>
                <p><strong>Speed:</strong> {speed.toFixed(2)}x</p>
                <p><strong>Style:</strong> {style.toFixed(2)}</p>
                <p><strong>Speaker Boost:</strong> {speakerBoost ? "On" : "Off"}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)} disabled={assigningVoice}>
              Cancel
            </Button>
            <Button onClick={assignVoiceToActor} disabled={assigningVoice || !selectedActorId}>
              {assigningVoice ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Settings className="w-4 h-4 mr-2" />
                  Assign Voice
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
